import Field from 'components/Form/Field';
import Icon from 'components/Icon';
import KeyValue from 'components/Layout/KeyValue';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import {H2} from 'components/Typography';
import Value from 'components/Value';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {Backend} from 'types/api';

const HELP_CENTER_PEPPOL_SEND_URL = 'help-center-peppol-send-url'; // TODO
const HELP_CENTER_HOW_TO_REGISTER_URL = 'help-center-how-to-register-url'; // TODO
const REGISTRATION_FORM_URL = 'registration-form-url'; // TODO
const MAKELEAPS_MAIL_ADDRESS = 'success@makeleaps.com';

export type PeppolSendBackendData = Backend<{
    registration: Registration;
}>;
type Registration = {
    companyAddress: string;
    companyName: string;
    corporateNumber: string;
    lastUpdated: string;
    peppolId: string;
    representative: string;
    representativeEmail: string;
    taxRegistrationNumber: string;
};

const PeppolSend = () => {
    const {
        data: {registration},
    } = useBackend<PeppolSendBackendData>();

    const helpCenterLink = (
        <Link
            aria-label="help-center"
            href={HELP_CENTER_PEPPOL_SEND_URL}
            isOpenInNewTab={true}
            key="help-center"
        >
            {t`Help Center`}
        </Link>
    );

    const peppolOrgLink = (
        <a
            aria-label="peppol-org"
            className="text-light-secondary underline hover:text-light-secondary"
            href="https://peppol.org/"
            key="peppol-org"
            target="_blank"
        >
            peppol.org
        </a>
    );

    const digitalAgencyLink = (
        <a
            aria-label="japanese-digital-agencys-digital-invoice-website"
            className="text-light-secondary underline hover:text-light-secondary"
            href="https://www.digital.go.jp/policies/electronic_invoice"
            key="japanese-digital-agencys-digital-invoice-website"
            target="_blank"
        >
            {t`Japanese Digital Agency's digital invoice website`}
        </a>
    );

    const howToRegisterLink = (
        <Link
            aria-label="help-center"
            href={HELP_CENTER_HOW_TO_REGISTER_URL}
            isOpenInNewTab={true}
            key="help-center-how-to-register"
        >
            {t`Help Center`}
        </Link>
    );
    const mailAddressLink = (
        <Link
            aria-label="contact"
            href={`mailto:${MAKELEAPS_MAIL_ADDRESS}`}
            isOpenInNewTab={true}
            key="mail-address"
        >
            {MAKELEAPS_MAIL_ADDRESS}
        </Link>
    );

    return (
        <div className="text-base">
            <NavigationTabs.Partner selection="integrations" />
            <SectionHeader
                subtitle={t`Send Documents as data across the Peppol network.`}
                title={t`Peppol Send (via Fast Accounting Ltd)`}
            />
            <p className="mt-6">
                {jt`The Peppol* Send integration includes Peppol Document Templates, the Peppol Send Sending method in your Sending List, and other settings to allow send digital invoices on the Peppol Network using MakeLeaps Document creation and Sending workflows. For details about MakeLeaps Peppol Send capabilities, please see our ${helpCenterLink} (Japanese).`}
            </p>
            <p className="mt-4">{t`Documents sent via this integration are transmitted through the Peppol Access Point operated by Fast Accounting Co., Ltd. **`}</p>
            <p className="mt-3 text-xs font-normal text-light-secondary">
                {jt`* Peppol is an international standard for the electronic exchange of business documents that has been adopted in Japan, Europe and elsewhere around the world. Learn more at ${peppolOrgLink} or at the ${digitalAgencyLink}.`}
            </p>
            <p className="text-xs font-normal text-light-secondary">
                {t`** Use of Peppol Send requires agreeing to Fast Accounting Co., Ltd's Terms of Service and basic contract terms.`}
            </p>
            <hr />
            <H2>{t`How to Register`}</H2>
            <p>{jt`1. Check the requirements in our ${howToRegisterLink}`}</p>
            <p>{t`2. Download and fill out the registration form`}</p>
            <LinkButton
                className="mb-2 mt-4"
                download
                href={REGISTRATION_FORM_URL}
                kind="secondary"
                size="base"
            >
                {t`Registration form`}
                <Icon className="ml-2 flex-none" icon="download" size="base" />
            </LinkButton>
            <p>{jt`3. Send the form to our success team at ${mailAddressLink}`}</p>
            <hr />
            <H2>{t`Current Registration Information`}</H2>
            {registration ? (
                <KeyValue className="ml-4 mt-4">
                    <Field label={t`Peppol Registration Status`} type="value">
                        <Value value={{type: 'string', value: t`Active`}} />
                    </Field>
                    <Field
                        label={t`Peppol Registration last updated`}
                        type="value"
                    >
                        <Value
                            value={{
                                type: 'string',
                                value: registration.lastUpdated,
                            }}
                        />
                    </Field>
                    <Field label={t`Peppol ID`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: registration.peppolId,
                            }}
                        />
                    </Field>
                    <Field label={t`Registered Company Name`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: registration.companyName,
                            }}
                        />
                    </Field>
                    <Field label={t`Registered Company Address`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: registration.companyAddress,
                            }}
                        />
                    </Field>
                    <Field label={t`Corporate Number`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: registration.corporateNumber,
                            }}
                        />
                    </Field>
                    <Field label={t`Tax Registration Number`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: registration.taxRegistrationNumber,
                            }}
                        />
                    </Field>
                    <Field label={t`Peppol Representative`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: registration.representative,
                            }}
                        />
                    </Field>
                    <Field label={t`Peppol Representative Email`} type="value">
                        <Value
                            value={{
                                type: 'string',
                                value: registration.representativeEmail,
                            }}
                        />
                    </Field>
                </KeyValue>
            ) : (
                <p>{t`There is no registration information to display.`}</p>
            )}
            <hr />
            <H2>{t`Peppol Send Cancellation`}</H2>
            <p className="mb-6">{t`Contact MakeLeaps Support if you wish to cancel your Peppol Registration. You will not be able to cancel your MakeLeaps Account if you have an Active Peppol Registration.`}</p>
        </div>
    );
};

export default PeppolSend;
